/**
 * Charts Resources
 *
 * Provides access to @classic-homes/charts-core package documentation.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import chartsCatalog from '../data/charts-catalog.json' with { type: 'json' };
import type { ChartsCatalog } from '../types.js';

const catalog = chartsCatalog as unknown as ChartsCatalog;

/**
 * Register charts-related resources
 */
export function registerChartsResources(server: McpServer): void {
  // Full charts catalog
  server.resource('Charts Core Package', 'charts://all', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog, null, 2),
      },
    ],
  }));

  // Theme module
  server.resource('Charts Theme', 'charts://theme', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.theme.importPath,
            description: catalog.subpackages.theme.description,
            functions: catalog.subpackages.main.modules.theme.functions,
            themeDetails: catalog.themeDetails,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Types
  server.resource('Charts Types', 'charts://types', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.types.importPath,
            description: 'TypeScript types exported from @classic-homes/charts-core',
            props: catalog.types.props,
            data: catalog.types.data,
            events: catalog.types.events,
            theme: catalog.types.theme,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Utilities (format + accessibility)
  server.resource('Charts Utilities', 'charts://utils', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.main.importPath,
            description: 'Utility functions for formatting and accessibility',
            format: catalog.subpackages.main.modules.format,
            accessibility: catalog.subpackages.main.modules.accessibility,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Quick reference
  server.resource('Charts Quick Reference', 'charts://quick-reference', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'text/markdown',
        text: `# Charts Core Quick Reference

## Installation

\`\`\`bash
${catalog.quickReference.installation}
\`\`\`

## Basic Usage

\`\`\`typescript
${catalog.quickReference.basicUsage}
\`\`\`

## Subpath Imports

**Theme subpath:**
\`\`\`typescript
${catalog.quickReference.themeSubpath}
\`\`\`

**Types subpath:**
\`\`\`typescript
${catalog.quickReference.typesSubpath}
\`\`\`

## Peer Dependency

\`${catalog.quickReference.peerDependency}\`

## Available Charts (13)

AreaChart, BarChart, LineChart, PieChart, DonutChart, CandlestickChart, FunnelChart, GaugeChart, HeatmapChart, RadarChart, SankeyChart, ScatterChart, TreemapChart

## Theme Exports

- \`generateLightTheme()\` - Generate light theme from tokens
- \`generateDarkTheme()\` - Generate dark theme from tokens
- \`lightTheme\` - Pre-generated light theme
- \`darkTheme\` - Pre-generated dark theme
- \`THEME_LIGHT\` - Constant: 'classic-light'
- \`THEME_DARK\` - Constant: 'classic-dark'

## Formatting Utilities

- \`formatNumber(value, options?)\` - Format with thousand separators
- \`formatCurrency(value, currency?, locale?)\` - Format as currency
- \`formatPercent(value, decimals?, locale?)\` - Format as percentage
- \`formatDate(date, options?, locale?)\` - Format date
- \`abbreviateNumber(value, decimals?)\` - Abbreviate to K/M/B/T
- \`createTooltipFormatter(options?)\` - Create ECharts tooltip formatter

## Accessibility Utilities

- \`generateLineChartDescription(title, data)\` - Accessible description with trends
- \`generateBarChartDescription(title, data)\` - Description with max/min
- \`generatePieChartDescription(title, data, isDonut?)\` - Description with top segments
- \`generateScatterChartDescription(title, data)\` - Description with point counts
- \`generateDataTable(headers, rows)\` - Hidden HTML table for screen readers

## Data Structures

**Categories + Series (Line, Bar, Area):**
\`\`\`typescript
interface LineChartData {
  categories: string[];
  series: { name: string; data: number[]; color?: string }[];
}
\`\`\`

**Slices (Pie, Donut, Funnel):**
\`\`\`typescript
interface PieSliceData {
  name: string;
  value: number;
  color?: string;
}
\`\`\`

**Coordinate Pairs (Scatter):**
\`\`\`typescript
interface ScatterSeriesData {
  name: string;
  data: [number, number][];
  color?: string;
}
\`\`\`

**OHLC (Candlestick):**
\`\`\`typescript
interface CandlestickData {
  dates: string[];
  data: { open: number; close: number; high: number; low: number }[];
  volume?: number[];
}
\`\`\`

**Nodes + Links (Sankey):**
\`\`\`typescript
interface SankeyData {
  nodes: { name: string; color?: string }[];
  links: { source: string; target: string; value: number }[];
}
\`\`\`
`,
      },
    ],
  }));

  // Props types only
  server.resource('Charts Props Types', 'charts://props', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog.types.props, null, 2),
      },
    ],
  }));

  // Data types only
  server.resource('Charts Data Types', 'charts://data', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog.types.data, null, 2),
      },
    ],
  }));

  // Event types only
  server.resource('Charts Event Types', 'charts://events', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog.types.events, null, 2),
      },
    ],
  }));
}
